import {
  ClockControlSettings,
  KnockOutSettings,
  OneOnOneSettings,
  SeanceSettingsDto,
  SwissSettings,
  Tournament,
  TournamentAdditionalScoreDto,
  TournamentVideoSettings,
  UpdateTournamentRequest,
} from '@features/shared/api/typings';
import { gameTimeControlConfig } from '@features/tournament/constants';
import { TournamentEditFormValues } from '@features/tournament/ui/components/TournamentSettings/typings';
import { VIDEO_SERVERS } from '@features/video/constants';

export const OPEN_WITH_PRE_MODERATION_ENTRANCE = 'OpenWithPreModeration';

const DEFAULT_CLOCK_CONTROL_RAPID: ClockControlSettings = {
  initialSeconds: gameTimeControlConfig.Rapid.defaultInitialSeconds,
  addSecondsPerMove: gameTimeControlConfig.Rapid.defaultSecondsAdded,
};
const DEFAULT_CLOCK_CONTROL_BLITZ: ClockControlSettings = {
  initialSeconds: gameTimeControlConfig.Blitz.defaultInitialSeconds,
  addSecondsPerMove: gameTimeControlConfig.Blitz.defaultSecondsAdded,
};
const DEFAULT_ONE_ON_ONE_SETTINGS: OneOnOneSettings = {
  gamesCount: 2,
  oneOnOneColorAllocation: 'Random',
  tieSettings: {
    additionalGamesCount: 0,
    clockControl: 'Rapid',
    clockControlSettingsPlayer1: DEFAULT_CLOCK_CONTROL_RAPID,
    clockControlSettingsPlayer2: DEFAULT_CLOCK_CONTROL_RAPID,
  },
};
const DEFAULT_KNOCKOUT_SETTINGS: KnockOutSettings = {
  gamesPerRound: 2,
  gamesPerFinalRound: 4,
  draftOrder: 'ByElo',
  maxPartiesCount: null,
  tieSettings: {
    additionalGamesCount: 2,
    additionalGamesFinalCount: 4,
    clockControl: 'Blitz',
    clockControlSettingsPlayer1: DEFAULT_CLOCK_CONTROL_BLITZ,
    clockControlSettingsPlayer2: DEFAULT_CLOCK_CONTROL_BLITZ,
  },
  armageddonSettings: {
    clockControl: 'Blitz',
    clockControlSettingsPlayer1: {
      initialSeconds: 5 * 60,
      addSecondsPerMove: 0,
    },
    clockControlSettingsPlayer2: {
      initialSeconds: 4 * 60,
      addSecondsPerMove: 0,
    },
  },
};
const DEFAULT_SWISS_SETTINGS: SwissSettings = {
  roundsCount: 1,
  seedNumberAllocationType: 'ByElo',
};

const DEFAULT_SEANCE_SETTINGS: SeanceSettingsDto = {
  maxPlayers: 10,
  seancerSide: 'White',
};

const DEFAULT_VIDEO_SETTINGS: TournamentVideoSettings = {
  enable: false,
  servers: [VIDEO_SERVERS[0]!],
  roomCapacity: 20,
  openBeforeStartMinutes: 30,
};

export function getDefaultValuesFromTournament(tournament: Tournament): TournamentEditFormValues {
  const {
    startTimestampMs,
    teamModeSettings,
    privateTeamModeSettings,
    roundRobinSettings,
    oneOnOneSettings,
    knockOutSettings,
    swissSettings,
    seanceSettings,
  } = tournament;

  return {
    ...tournament,
    entrance:
      tournament.participantPreModerationEnabled && tournament.entrance === 'Open'
        ? OPEN_WITH_PRE_MODERATION_ENTRANCE
        : tournament.entrance,
    registrationClosedBeforeStartMinutes: tournament.registrationClosedBeforeStartMinutes,
    teamModeSettings: teamModeSettings || {
      size: 2,
      numberInTeamMode: 'Shift',
      primaryScoringMethod: 'MatchPoints',
    },
    privateTeamModeSettings: privateTeamModeSettings || {
      playersToConsiderForScore: 2,
      tieBreakers: [],
    },
    startTime: startTimestampMs ? new Date(startTimestampMs) : null,
    roundRobinSettings: roundRobinSettings || {
      roundsCount: 1,
      maxPartiesCount: null,
    },
    oneOnOneSettings: oneOnOneSettings || {
      ...DEFAULT_ONE_ON_ONE_SETTINGS,
    },
    knockOutSettings: knockOutSettings || {
      ...DEFAULT_KNOCKOUT_SETTINGS,
    },
    swissSettings: swissSettings || {
      ...DEFAULT_SWISS_SETTINGS,
    },
    seanceSettings: seanceSettings
      ? {
          maxPlayers: seanceSettings.maxPlayers,
          seancerSide: seanceSettings.seancerSide,
          // FIXME: бэкенд пока игнорирует это поле, плюс в нём слишком много лишнего (UserDto)
          seancer: seanceSettings.seancer ? undefined : undefined,
        }
      : {
          ...DEFAULT_SEANCE_SETTINGS,
        },
    sponsors: tournament.sponsors || [],
    videoSettings: tournament.videoSettings ?? { ...DEFAULT_VIDEO_SETTINGS },
    initialFen: tournament.initialFen,
    isBlind: tournament.isBlind,
    orgSettings: tournament.orgSettings
      ? {
          ...tournament.orgSettings,
          arbiters: tournament.orgSettings.arbiters?.map((a) => ({ name: a })) || [],
        }
      : undefined,
    customRoundDelays: tournament.customRoundDelays?.map((delay) => ({
      roundNumberAfter: String(delay.roundNumberAfter),
      delayMinutes: String(delay.delaySeconds / 60),
    })),
  };
}

export function getSubmitValuesFromForm(values: TournamentEditFormValues): UpdateTournamentRequest {
  const result: UpdateTournamentRequest = {
    ...values,
    entrance: values.entrance === OPEN_WITH_PRE_MODERATION_ENTRANCE ? 'Open' : values.entrance,
    participantPreModerationEnabled: values.entrance === OPEN_WITH_PRE_MODERATION_ENTRANCE,
    mode: values.system === 'Seance' ? 'Private' : values.mode,
    additionalScoreOrder: values.additionalScoreOrder.filter(
      (v) => !!v,
    ) as TournamentAdditionalScoreDto[],
    startTimestampMs: values.startTime ? values.startTime.getTime() : Date.now() + 10 * 60 * 1000,
    orgSettings: values.rcfRankCalculated
      ? {
          chiefArbiter: values.orgSettings?.chiefArbiter || null,
          organizer: values.orgSettings?.organizer || null,
          arbiters: values.orgSettings?.arbiters.map((a) => a.name) || [],
        }
      : undefined,
    customRoundDelays: values.customRoundDelays
      ? values.customRoundDelays.map((delay) => ({
          roundNumberAfter: Number(delay.roundNumberAfter),
          delaySeconds: Number(delay.delayMinutes) * 60,
        }))
      : null,
  };
  if (result.mode !== 'Team' && result.mode !== 'PrivateTeam') delete result.teamModeSettings;
  if (result.system !== 'RoundRobin') {
    if (result.mode !== 'PrivateTeam') delete result.privateTeamModeSettings;
    delete result.roundRobinSettings;
  }
  if (result.system !== 'OneOnOne') delete result.oneOnOneSettings;
  if (result.system !== 'Knockout') delete result.knockOutSettings;
  if (!result.clockControlSettingsPlayer2)
    result.clockControlSettingsPlayer2 = result.clockControlSettingsPlayer1;
  if (result.system !== 'Seance') delete result.seanceSettings;
  if (result.system !== 'Swiss') delete result.swissSettings;

  if (result.system === 'RoundRobin' || result.system === 'Knockout')
    result.removePlayerSetting = 'DoNotRemovePlayers';

  // Видеоконтроль выключен — настроек не шлем: сервер проверяет время открытия трансляции
  // против закрытия регистрации и для выключенного. В контракте поле не nullable, и так
  // останется: на бэкенде это сложно, поэтому приведение типа
  if (!result.videoSettings?.enable) {
    result.videoSettings = null as unknown as undefined;
  }

  return result;
}
